import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/PatientHistorySummary.css';

const PatientHistorySummary = ({ patientId, onClose }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState({
    visits: [],
    labTests: [],
    admissions: [],
    procedures: []
  });
  const [collapsedSections, setCollapsedSections] = useState({
    visits: true,
    labTests: true,
    admissions: true,
    procedures: true
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    const fetchPatientHistory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients/${patientId}/history-summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHistoryData(response.data);
      } catch (err) {
        console.error('Error fetching patient history:', err);
        setError('Failed to load patient history');
      } finally {
        setLoading(false);
      }
    };

    fetchPatientHistory();
  }, [patientId, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateDaysInHospital = (admissionDate, dischargeDate) => {
    if (!admissionDate) return 0;
    const start = new Date(admissionDate);
    const end = dischargeDate ? new Date(dischargeDate) : new Date();
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const parsePrescription = (prescriptionData) => {
    try {
      if (!prescriptionData) return null;
      const parsed = typeof prescriptionData === 'string' ? JSON.parse(prescriptionData) : prescriptionData;
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      console.error('Error parsing prescription:', error);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="patient-history-summary loading">
        <div className="summary-header">
          <h3>Patient History</h3>
          {onClose && <button className="close-btn" onClick={onClose}>×</button>}
        </div>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-history-summary error">
        <div className="summary-header">
          <h3>Patient History</h3>
          {onClose && <button className="close-btn" onClick={onClose}>×</button>}
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const hasNoHistory = 
    historyData.visits.length === 0 && 
    historyData.labTests.length === 0 && 
    historyData.admissions.length === 0 && 
    historyData.procedures.length === 0;

  return (
    <div className="patient-history-summary">
      <div className="summary-header">
        <h3>Patient History</h3>
        {onClose && <button className="close-btn" onClick={onClose}>×</button>}
      </div>

      {hasNoHistory ? (
        <div className="no-history">
          <p>No previous medical history available for this patient.</p>
        </div>
      ) : (
        <div className="summary-content">
          {/* Recent Visits Section */}
          {historyData.visits.length > 0 && (
            <div className="history-section">
              <h4 className="section-header" onClick={() => toggleSection('visits')}>
                <span className="toggle-icon">{collapsedSections.visits ? '▶' : '▼'}</span>
                Recent Visits ({historyData.visits.length})
              </h4>
              {!collapsedSections.visits && (
              <div className="history-items">
                {historyData.visits.map((visit, index) => (
                  <div key={visit.visit_id || index} className="history-item visit-item">
                    <div className="item-header">
                      <span className="item-date">{formatDate(visit.visit_date)}</span>
                      <span className="item-badge visit-badge">{visit.branch}</span>
                    </div>
                    <div className="item-details">
                      <div className="detail-row">
                        <span className="detail-label">Doctor:</span>
                        <span className="detail-value">{visit.assigned_doctor}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Diagnosis:</span>
                        <span className={`detail-value ${!visit.diagnosis ? 'no-data' : ''}`}>
                          {visit.diagnosis || 'No data Available'}
                        </span>
                      </div>
                      {visit.purpose && (
                        <div className="detail-row">
                          <span className="detail-label">Purpose:</span>
                          <span className="detail-value">{visit.purpose}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Treatment Plan:</span>
                        <span className={`detail-value ${!visit.proposed_treatment ? 'no-data' : ''}`}>
                          {visit.proposed_treatment || 'No data Available'}
                        </span>
                      </div>
                      {visit.prescription_items && (
                        <div className="detail-row prescription-row">
                          <span className="detail-label">Prescription:</span>
                          <span className="detail-value prescription-badge">
                            {parsePrescription(visit.prescription_items) ? (
                              <span className="prescription-indicator">
                                Prescribed ({parsePrescription(visit.prescription_items).length} item{parsePrescription(visit.prescription_items).length > 1 ? 's' : ''})
                                <div className="prescription-tooltip">
                                  {parsePrescription(visit.prescription_items).map((med, idx) => (
                                    <div key={idx} className="prescription-item">
                                      <div className="med-name">{med.name || med.item_name || 'Unknown medication'}</div>
                                      {med.duration && <div className="med-detail">Duration: {med.duration}</div>}
                                      {med.quantity && <div className="med-detail">Qty: {med.quantity}</div>}
                                      {med.instructions && <div className="med-detail">{med.instructions}</div>}
                                    </div>
                                  ))}
                                </div>
                              </span>
                            ) : (
                              'Prescription available'
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* Recent Lab Tests Section */}
          {historyData.labTests.length > 0 && (
            <div className="history-section">
              <h4 className="section-header" onClick={() => toggleSection('labTests')}>
                <span className="toggle-icon">{collapsedSections.labTests ? '▶' : '▼'}</span>
                Recent Lab Tests ({historyData.labTests.length})
              </h4>
              {!collapsedSections.labTests && (
              <div className="history-items">
                {historyData.labTests.map((test, index) => (
                  <div key={test.id || index} className="history-item lab-item">
                    <div className="item-header">
                      <span className="item-date">{formatDate(test.createdAt)}</span>
                      <span className={`item-badge status-${test.status}`}>
                        {test.status}
                      </span>
                    </div>
                    <div className="item-details">
                      <div className="detail-row">
                        <span className="detail-label">Requested by:</span>
                        <span className="detail-value">{test.requested_by}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Tests:</span>
                        <span className="detail-value test-summary">
                          {test.test_summary}
                        </span>
                      </div>
                      {test.request_type && (
                        <div className="detail-row">
                          <span className="detail-label">Type:</span>
                          <span className="detail-value">{test.request_type}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* Admissions Section */}
          {historyData.admissions.length > 0 && (
            <div className="history-section">
              <h4 className="section-header" onClick={() => toggleSection('admissions')}>
                <span className="toggle-icon">{collapsedSections.admissions ? '▶' : '▼'}</span>
                Admissions ({historyData.admissions.length})
              </h4>
              {!collapsedSections.admissions && (
              <div className="history-items">
                {[...historyData.admissions].sort((a, b) => {
                  // Active admissions (no discharge_date) come first
                  if (!a.discharge_date && b.discharge_date) return -1;
                  if (a.discharge_date && !b.discharge_date) return 1;
                  // Otherwise sort by admission date descending
                  return new Date(b.admission_date) - new Date(a.admission_date);
                }).map((admission, index) => {
                  const daysInHospital = calculateDaysInHospital(
                    admission.admission_date, 
                    admission.discharge_date
                  );
                  const isActive = !admission.discharge_date;

                  return (
                    <div key={admission.admission_id || index} className={`history-item admission-item ${isActive ? 'active' : ''}`}>
                      <div className="item-header">
                        <span className="item-date">{formatDate(admission.admission_date)}</span>
                        <span className={`item-badge ${isActive ? 'active-badge' : 'discharge-badge'}`}>
                          {isActive ? 'Active' : 'Discharged'}
                        </span>
                      </div>
                      <div className="item-details">
                        <div className="detail-row">
                          <span className="detail-label">Duration:</span>
                          <span className="detail-value">
                            {daysInHospital} {daysInHospital === 1 ? 'day' : 'days'}
                            {isActive && ' (ongoing)'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Ward:</span>
                          <span className="detail-value">{admission.ward} {admission.bed && `- Bed ${admission.bed}`}</span>
                        </div>
                        {admission.admissionReason && (
                          <div className="detail-row">
                            <span className="detail-label">Reason:</span>
                            <span className="detail-value">{admission.admissionReason}</span>
                          </div>
                        )}
                        {admission.diagnosis && (
                          <div className="detail-row">
                            <span className="detail-label">Diagnosis:</span>
                            <span className="detail-value">{admission.diagnosis}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* Procedures/Surgeries Section */}
          {historyData.procedures.length > 0 && (
            <div className="history-section">
              <h4 className="section-header" onClick={() => toggleSection('procedures')}>
                <span className="toggle-icon">{collapsedSections.procedures ? '▶' : '▼'}</span>
                Procedures & Surgeries ({historyData.procedures.length})
              </h4>
              {!collapsedSections.procedures && (
              <div className="history-items">
                {historyData.procedures.map((procedure, index) => (
                  <div key={procedure.procedure_id || index} className="history-item procedure-item">
                    <div className="item-header">
                      <span className="item-date">
                        {formatDate(procedure.procedure_date || procedure.scheduled_date)}
                      </span>
                      <span className={`item-badge procedure-${procedure.procedure_type}`}>
                        {procedure.procedure_type}
                      </span>
                    </div>
                    <div className="item-details">
                      <div className="detail-row">
                        <span className="detail-label">Procedure:</span>
                        <span className="detail-value">{procedure.procedure_name}</span>
                      </div>
                      {procedure.surgeon_name && (
                        <div className="detail-row">
                          <span className="detail-label">Surgeon:</span>
                          <span className="detail-value">{procedure.surgeon_name}</span>
                        </div>
                      )}
                      {procedure.notes && (
                        <div className="detail-row">
                          <span className="detail-label">Notes:</span>
                          <span className="detail-value">{procedure.notes}</span>
                        </div>
                      )}
                      {procedure.status && (
                        <div className="detail-row">
                          <span className="detail-label">Status:</span>
                          <span className="detail-value">{procedure.status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientHistorySummary;
